import * as signalR from "@microsoft/signalr";
import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { Col, Row, Input } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Afr, DocumentListEntry, getDocumentList, getSelectedAfr, loadAfr, pullDocumentList, setToImportedAfr } from '../../redux/AfrSlice';
import {
    AFRDocumentStarter, DocumentNotification, downloadDocumentById, getAFRReport, selectDocuments, updateDocumentNotification
} from '../../redux/DocumentsSlice';
import { Entity, getNameForAfr, getSelectedEntity, selectedEntity } from '../../redux/EntitySlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { ReportTile } from '../core/ReportTile';
import DisplayNotificationUpdates from '../feature/DisplayNotificationUpdates';
import Page from '../Page';
import { IReportFormat, getSuffix } from '../feature/SystemReports';
import { TaxonomyTypeEnum } from "../../redux/TaxonomyVersionSlice";

const AfrReportsCore = (props: { taxonomyType: TaxonomyTypeEnum, publicreports: boolean; isForPublicReports?: boolean | null; }) => {

    const dispatch = useAppDispatch();
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const afrYear = currentAfr ? currentAfr.year : 0;
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const reportFormats: Array<IReportFormat> = [
        { label: 'PDF', value: 'PDF' },
        { label: 'IXBRL', value: 'IXBRL' }];

    const [reportDocumentList, setReportDocumentList] = React.useState<Array<DocumentListEntry>>([]);
    const [selectedReportFormat, setSelectedReportFormat] = React.useState<{ value: string; label: string; }>({ label: 'PDF', value: 'PDF' });
    const [afrXhtmlDownloadIsInProgress, setAfrXhtmlDownloadIsInProgress] = React.useState<boolean>(false);
    const [afrXbrlDownloadIsInProgress, setAfrXbrlDownloadIsInProgress] = React.useState<boolean>(false);
    const [afrPdfDownloadIsInProgress, setAfrPdfDownloadIsInProgress] = React.useState<boolean>(false);
    const [spfsPdfDownloadIsInProgress, setSpfsPdfDownloadIsInProgress] = React.useState<boolean>(false);
    const [spfsXhtmlDownloadIsInProgress, setSpfsXhtmlDownloadIsInProgress] = React.useState<boolean>(false);
    const [documentListItemIsLoading, setSelectedDocumentListItemIsLoading] = React.useState<boolean>(false);
    const [selectedDocumentListItem, setSelectedDocumentListItem] = React.useState<string>("");


    const useSPFS = false;
    const getEntityName = function (e: Entity | null, afr: Afr | null): string {
        if (!e) {
            return "";
        }
        if (!afr) {
            return e.name;
        }
        return getNameForAfr(e, afr);
    }
    const getEntityCode = function (e: Entity | null, afr: Afr | null): string {
        if (!e) {
            return "";
        }
        if (!afr || !afr.owner) {
            return e.code;
        }

        return afr.owner.code;
    }

    const documentSelector = useAppSelector(selectDocuments);
    const documentContent = documentSelector.content;
    var title = "AFR Specific Reports";
    var reportName: string = "AFR";
    var expandedReportName: string = "Annual Financial Report";
    var reportQuarter = "";
    switch (props.taxonomyType) {
        case TaxonomyTypeEnum.COURT_REPORTING_939:
            title = "Court Report 939.185 Specific Reports"
            reportName = "Court Reporting 939.185";
            expandedReportName = reportName;
            if (currentAfr && currentAfr.logicalPeriod) {
                reportQuarter = currentAfr.logicalPeriod.replace("Q_", " Q");
            }
            break;
        case TaxonomyTypeEnum.COURT_REPORTING_29:
            title = "Court Report 29.0085 Specific Reports"
            reportName = "Court Reporting 29.0085";
            expandedReportName = reportName;
            break;
    }

    if (props.publicreports) {
        title = "Public Reports";
    }

    async function startReport(reportType: string, outputType: string, fileName: string) {
        if (currentAfr !== null &&
            entity !== null) {
            const afrData: AFRDocumentStarter = {
                afrYear: currentAfr.year,
                entityCode: currentAfr.afrId,
                fileType: reportType,
                outputType: outputType,
                populate: 'CURRENT_YEAR'
            };
            const response = await dispatch(getAFRReport(afrData));
            const doc = unwrapResult(response);
            if (doc != null) {

                //TODO make this a generic function in document slice
                //that takes mime type, base64 encoded string, filename
                //and returns a blob.
                //TODO expand to handle xhtml and excel files
                var url = "data:" + doc.mimeType + ";base64," + doc.content;
                await fetch(url)
                    .then(res => res.blob())
                    .then(otho => {

                        const data = window.URL.createObjectURL(otho);
                        var link = document.createElement("a");
                        link.href = data;
                        link.download = fileName; //"report.xhtml";
                        link.click();
                        setTimeout(function () {
                            // For Firefox it is necessary to delay revoking the ObjectURL
                            window.URL.revokeObjectURL(data);
                        }, 100);
                    });
            }

            return doc;
        }
    }
    const generateReportName = function (suffix: string): string {
        var result: string = reportName;
        if (currentAfr && currentAfr.taxonomyType) {
            switch (currentAfr.taxonomyType) {
                case TaxonomyTypeEnum.COURT_REPORTING_939:
                    result = "939 - " + getEntityName(entity, currentAfr) + " - " + reportQuarter + " - " + afrYear
                    break;

                default:
                    result = reportName + " " + getEntityName(entity, currentAfr) + " " + afrYear
                    break;
            }

        }

        return result + suffix;
    }


    async function generateAFRReportPDF() {
        setAfrPdfDownloadIsInProgress(true);
        try {
            var rtype = props.publicreports ? "PUBLICAFR" : "AFR";
            await startReport(rtype, "PDF", generateReportName( ".pdf" ));
        } catch (err) {
            console.log(err);
            setAfrPdfDownloadIsInProgress(false);
        }
        setAfrPdfDownloadIsInProgress(false);
    }

    async function generateSPFSReportPDF() {
        setSpfsPdfDownloadIsInProgress(true);
        try {
            await startReport("SPFS", "PDF", "SPFS " + getEntityName(entity, currentAfr) + " " + afrYear + ".pdf");
            //            await startReport("SPFS", "Special Purpose Financial Statement.zip");
        } catch (err) {
            console.log(err);
            setSpfsPdfDownloadIsInProgress(false);
        }
        setSpfsPdfDownloadIsInProgress(false);
    }
    async function generateAFRReportXHTML() {
        setAfrXhtmlDownloadIsInProgress(true);
        try {
            var rtype = props.publicreports ? "PUBLICAFR" : "AFR";
            await startReport(rtype, "XHTML", generateReportName(".xhtml"));
        } catch (err) {
            console.log(err);
            setAfrXhtmlDownloadIsInProgress(false);
        }
        setAfrXhtmlDownloadIsInProgress(false);
    }

    async function generateAFRReportIXBRL() {
        setAfrXbrlDownloadIsInProgress(true);
        try {
            var rtype = props.publicreports ? "PUBLICAFR" : "AFR";
            await startReport(rtype, "IXBRL", generateReportName( " - iXBRL.xhtml"));
        } catch (err) {
            console.log(err);
            setAfrXbrlDownloadIsInProgress(false);
        }
        setAfrXbrlDownloadIsInProgress(false);
    }

    async function generateSPFSReportXHTML() {
        setSpfsXhtmlDownloadIsInProgress(true);
        try {
            await startReport("SPFS", "XHTML", "SPFS " + getEntityName(entity, currentAfr) + " " + afrYear + ".xhtml");
            //            await startReport("SPFS", "Special Purpose Financial Statement.zip");
        } catch (err) {
            console.log(err);
            setSpfsXhtmlDownloadIsInProgress(false);
        }
        setSpfsXhtmlDownloadIsInProgress(false);
    }

    const handleClickDownload = (docId: string | null) => async (event: any) => {
        if (docId) {
            setSelectedDocumentListItem(docId);
            setSelectedDocumentListItemIsLoading(true);
            await dispatch(downloadDocumentById(docId));
            setSelectedDocumentListItemIsLoading(false);
            setSelectedDocumentListItem("");
        }
    }
    function filterForAffiliateDocs(entity: any | null, doc: DocumentListEntry, docList: DocumentListEntry[]) {
        if (!entity) {
            return;
        }
        if (!entity.affiliation) {
            // If the selected entity is a primary - add the document
            docList.push(doc);
            return;
        }
        var affCode: string | null = doc.document.affiliate;
        if (!affCode) {
            docList.push(doc); // bug 28701
            return;
        }
        // We're here because the entity has an affiliation, and the document does as well. 
        if (entity.doingBusinessAs) {
            if (affCode === entity.doingBusinessAs.currentEntity || affCode === entity.doingBusinessAs.previousEntity) {
                docList.push(doc);
                return;
            }
        }
        if (entity.affiliation && entity.code == affCode) {
            docList.push(doc);
            return;
        }
    }
    React.useEffect(() => {
        if (props.publicreports == true) {
            if (currentAfr != null && entity != null) {
                var AFRId = currentAfr.afrId; //entity.code + "^" + currentAfr.year.toString();
                //response has to be awited to give time for data.
                (async () => {
                    const response = await dispatch(loadAfr(AFRId));
                    const afrData = unwrapResult(response);
                    if (afrData != null) {
                        var docList: DocumentListEntry[] = [];
                        getDocumentList(afrData).map((doc, idx) => filterForAffiliateDocs(entity, doc, docList));
                        setReportDocumentList(docList);
                        dispatch(setToImportedAfr({ afrId: afrData.afrId, year: afrData.year, status: "REPORT_MODE", owner: { ...afrData.owner } }));
                    }
                })();
            }
        }
    }, [afrYear]);

    function isReportFormatSelected(reportFormatValue: string): boolean {
        var result = false;

        if (reportFormatValue) {
            if (selectedReportFormat.value == reportFormatValue) {
                result = true;
            }
        }
        return result;
    }

    const reportFormatRender = (<div>
        <ul>
            {reportFormats.map(({ value, label }) => (
                <li key={value}>
                    <Input
                        type="radio"
                        value={value}
                        name="report-formats"
                        onChange={(e) => {
                            setSelectedReportFormat({ value: value, label: label });
                        }}
                        checked={isReportFormatSelected(label)} />
                    {label}
                </li>
            ))}
        </ul>
    </div>);
    
    return (
        <React.Fragment>
            <Page>
                <h5> {getEntityName(entity, currentAfr)} ({getEntityCode(entity, currentAfr)}), {afrYear}</h5>
                <Row>
                    <Col>
                        <ReportTile
                            reportType="AFR"
                            entityName={getEntityName(entity, currentAfr)}
                            entityCode={getEntityCode(entity, currentAfr)}
                            afrYear={afrYear}
                            isEnabled={true}
                            isForPublicReports={props.isForPublicReports}
                            clickAction={generateAFRReportPDF}
                            tileTitle={expandedReportName + " (PDF)"}
                            tileDescription={expandedReportName + " (PDF)"}
                            isLoading={afrPdfDownloadIsInProgress}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ReportTile
                            reportType="AFR"
                            entityName={getEntityName(entity, currentAfr)}
                            entityCode={getEntityCode(entity, currentAfr)}
                            afrYear={afrYear}
                            isEnabled={true}
                            isForPublicReports={props.isForPublicReports}
                            clickAction={generateAFRReportXHTML}
                            tileTitle={expandedReportName + " (Web)"}
                            tileDescription={expandedReportName + " (Web)"}
                            isLoading={afrXhtmlDownloadIsInProgress}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <ReportTile
                            reportType="AFR"
                            entityName={getEntityName(entity, currentAfr)}
                            entityCode={getEntityCode(entity, currentAfr)}
                            afrYear={afrYear}
                            isEnabled={true}
                            isForPublicReports={props.isForPublicReports}
                            clickAction={generateAFRReportIXBRL}
                            tileTitle={expandedReportName + " (iXBRL)"}
                            tileDescription={expandedReportName + " (iXBRL)"}
                            isLoading={afrXbrlDownloadIsInProgress }
                        />
                    </Col>
                </Row>
                <Row>
                    {
                        useSPFS
                            ?
                            <Col>
                                <ReportTile
                                    reportType="CONSOLIDATED"
                                    entityName={getEntityName(entity, currentAfr)}
                                    entityCode={getEntityCode(entity, currentAfr)}
                                    afrYear={afrYear}
                                    clickAction={generateSPFSReportPDF}
                                    isEnabled={true}
                                    isForPublicReports={props.isForPublicReports}
                                    tileTitle="Consolidated Report (PDF)"
                                    tileDescription="Special Purpose Financial Statement (PDF)"
                                    isLoading={spfsPdfDownloadIsInProgress} />
                            </Col>
                    :
                            <div></div>
                    }
                </Row>
                <Row>
                    {
                        useSPFS ?
                            <Col>
                                <ReportTile
                                    reportType="CONSOLIDATED"
                                    entityName={getEntityName(entity, currentAfr)}
                                    entityCode={getEntityCode(entity, currentAfr)}
                                    afrYear={afrYear}
                                    clickAction={generateSPFSReportXHTML}
                                    isEnabled={true}
                                    isForPublicReports={props.isForPublicReports}
                                    tileTitle="Consolidated Report (XHTML)"
                                    tileDescription="Special Purpose Financial Statement (XHTML)"
                                    isLoading={spfsXhtmlDownloadIsInProgress}                        />
                            </Col>
                        : <div></div>
                    }
                </Row>

                {props.publicreports == true && reportDocumentList.length > 0 ?( reportDocumentList.map((option, idx) => (
                    <Row key={utilityHelper.generateGuid()}>
                        <Col>
                            <ReportTile
                                reportType={option.type}
                                entityName={getEntityName(entity, currentAfr)}
                                entityCode={getEntityCode(entity, currentAfr)}
                                afrYear={afrYear}
                                clickAction={handleClickDownload(option.document.documentId)}
                                isEnabled={true}
                                tileTitle={utilityHelper.toTitleCase(option.type)}
                                isLoading={documentListItemIsLoading && selectedDocumentListItem == option.document.documentId}
                                isForPublicReports={props.isForPublicReports}
                                tileDescription={option.document.affiliate ?
                                    option.name + ", " + utilityHelper.toTitleCase(option.type)
                                    : option.name} />
                        </Col>                     
                    </Row>
                ))) : (<></>)}
                <Row>
                    <Col>
                        {(!(utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)) ) && (<DisplayNotificationUpdates />)}
                    </Col>
                </Row>
            </Page>
        </React.Fragment>
    );
}

export default AfrReportsCore;